<template>
  <v-container class="alert-wizard-container">
    <v-btn
      class="wizard-exit-btn"
      elevation="0"
      solo
      text
      @click="closedFunc()"
    >
      Exit Builder
    </v-btn>
    <v-stepper
      v-model="currentStep"
      elevation="0"
      alt-labels
      class="wizard-container"
    >
      <AlertWizardHeader
        :alert="alert"
        :wizardSteps="wizardSteps"
        @step-changed="updateStep"
      ></AlertWizardHeader>
      <v-stepper-items>
        <v-stepper-content
          :step="wizardSteps.StartStep.id"
          class="alert-wizard-content-container"
        >
          Click 'Next' button to create your alert.
          <v-btn class="wizard-next-btn" color="primary"> Next </v-btn>
        </v-stepper-content>
        <v-stepper-content
          :step="wizardSteps.AoiStep.id"
          class="alert-wizard-content-container"
        >
          <AoiStep
            :alert="alert"
            :isActive="isAoi"
            @stepFinished="aoiStepFinished"
          ></AoiStep>
        </v-stepper-content>
        <v-stepper-content
          :step="wizardSteps.TripWiresStep.id"
          class="alert-wizard-content-container"
        >
          <TripWiresStep
            :alert="alert"
            @stepFinished="tripWiresStepFinished"
            @processingChange="tripWiresProcessingUpdate"
            :isActive="isTripwires"
          ></TripWiresStep>
        </v-stepper-content>
        <v-stepper-content
          :step="wizardSteps.FrequencyStep.id"
          class="alert-wizard-content-container"
        >
          <FrequencyStep
            :alert="alert"
            @stepFinished="frequencyStepFinished"
            :isActive="isFrequency"
          ></FrequencyStep>
        </v-stepper-content>
        <v-stepper-content
          :step="wizardSteps.ShareStep.id"
          class="alert-wizard-content-container"
        >
          <ShareStep
            :alert="alert"
            @step-finished="sharedStepFinished"
            :isActive="isShare"
          ></ShareStep>
        </v-stepper-content>
        <v-stepper-content
          :step="wizardSteps.SummaryStep.id"
          class="alert-wizard-content-container"
        >
          <SummaryStep
            :alert="alert"
            :isActive="isSummary"
            :showMap="currentStep === wizardSteps.SummaryStep.id"
          >
          </SummaryStep>
        </v-stepper-content>
      </v-stepper-items>
      <AlertWizardFooter
        :nextBtn="nextBtn"
        :prevBtn="prevBtn"
        :finishBtn="finishBtn"
      >
      </AlertWizardFooter>
    </v-stepper>
    <v-snackbar v-model="displayError" absolute>
      {{ errorMessage }}
      <v-btn color="pink" text @click="snackbar = false"> Close </v-btn>
    </v-snackbar>
  </v-container>
</template>
<script>
import lodashFind from "lodash/find";
import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_ALERTS,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
} from "@/store";
import { SAVE_USER_ALERT, CLOSE_ALERT_WIZARD } from "@/store/actions.type.js";
import { BASIC_ALERT } from "@/common/constants/alert.js";
import { validateAlert } from "@/services/alerts/alert.validation.js";
import { ALERT_AOI_DISPLAY_MAP } from "@/common/constants/alert.js";
import { EVENT_CATEGORIES, EVENT_ACTIONS } from "@/common/constants/analytics";

export default {
  name: "AlertWizard",
  components: {
    AlertWizardHeader: () =>
      import("@/components/alerts/wizard/AlertWizardHeader"),
    AlertWizardFooter: () =>
      import("@/components/alerts/wizard/AlertWizardFooter"),
    AoiStep: () => import("@/components/alerts/wizard/steps/AoiStep"),
    TripWiresStep: () =>
      import("@/components/alerts/wizard/steps/TripWiresStep"),
    FrequencyStep: () =>
      import("@/components/alerts/wizard/steps/FrequencyStep"),
    ShareStep: () => import("@/components/alerts/wizard/steps/ShareStep"),
    SummaryStep: () => import("@/components/alerts/wizard/steps/SummaryStep"),
  },
  data() {
    const wizardSteps = {
      StartStep: {
        id: 1,
        isValid: false,
      },
      AoiStep: {
        id: 2,
        isValid: false,
      },
      TripWiresStep: {
        id: 3,
        isValid: false,
        processing: false,
      },
      FrequencyStep: {
        id: 4,
        isValid: false,
      },
      ShareStep: {
        id: 5,
        isValid: false,
      },
      SummaryStep: {
        id: 6,
        isValid: false,
      },
    };
    return {
      errorMessage: "Invalid Error",
      displayError: false,
      currentStep: wizardSteps.AoiStep.id,
      alert: new BASIC_ALERT(),
      wizardSteps,
    };
  },
  computed: {
    ...mapGetters({
      savingAlert: MODULE_PREFIX_ALERTS + "saving",
      wizardDetails: MODULE_PREFIX_ALERTS + "wizardDetails",
      displayWizard: MODULE_PREFIX_ALERTS + "displayWizard",
      maps: MODULE_PREFIX_MAP + "maps",
      profile: MODULE_PREFIX_USER + "profile",
    }),
    summaryMap() {
      return this.maps[ALERT_AOI_DISPLAY_MAP];
    },
    isSummary() {
      return this.wizardSteps.SummaryStep.id === this.currentStep;
    },
    isAoi() {
      return this.wizardSteps.AoiStep.id === this.currentStep;
    },
    isTripwires() {
      return this.wizardSteps.TripWiresStep.id === this.currentStep;
    },
    isShare() {
      return this.wizardSteps.ShareStep.id === this.currentStep;
    },
    isFrequency() {
      return this.wizardSteps.FrequencyStep.id === this.currentStep;
    },
    isCurrentStepValid() {
      if (this.isSummary) {
        return true;
      }
      const currentStep = lodashFind(this.wizardSteps, {
        id: this.currentStep,
      });
      return currentStep.isValid;
    },
    showFinishButton() {
      if (!this.isSummary && this.alert && this.alert.id) {
        return true;
      }
      return false;
    },
    areAllStepsValid() {
      if (
        !this.wizardSteps.AoiStep.isValid ||
        !this.wizardSteps.TripWiresStep.isValid ||
        !this.wizardSteps.ShareStep.isValid ||
        !this.wizardSteps.FrequencyStep.isValid ||
        !this.wizardSteps.AoiStep.isValid
      ) {
        return false;
      }
      return true;
    },
    isProcessing() {
      return this.wizardSteps.TripWiresStep.processing;
    },
    nextBtn() {
      return {
        label: this.isSummary ? "SAVE" : "NEXT",
        isLoading: this.savingAlert,
        display: true,
        disabled:
          this.savingAlert || !this.isCurrentStepValid || this.isProcessing,
        func: this.routeToNext,
      };
    },
    prevBtn() {
      return {
        label: "",
        isLoading: false,
        display: !(this.wizardSteps.AoiStep.id === this.currentStep),
        disabled: this.savingAlert || !this.isCurrentStepValid,
        func: this.routeToPrevious,
      };
    },
    finishBtn() {
      return {
        label: "FINISH",
        isLoading: false,
        display: this.showFinishButton,
        disabled: this.savingAlert || !this.areAllStepsValid,
        func: this.routeToSummary,
      };
    },
  },
  methods: {
    updateStep({ step }) {
      this.currentStep = step.id;
    },
    async closedFunc() {
      await this.$store.dispatch(MODULE_PREFIX_ALERTS + CLOSE_ALERT_WIZARD, {});
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.alert_wizard,
        EVENT_ACTIONS.clicked,
        "Exit Builder"
      );
    },
    aoiStepFinished({ isValid }) {
      this.wizardSteps.AoiStep.isValid = isValid;
      const validationResults = validateAlert(this.alert);
      if (validationResults.type === false) {
        this.wizardSteps.TripWiresStep.isValid = false;
      } else if (this.alert.id || this.wizardDetails.clone) {
        this.wizardSteps.TripWiresStep.isValid = validationResults.type;
      }
    },
    tripWiresStepFinished({ isValid }) {
      this.wizardSteps.TripWiresStep.isValid = isValid;
      const validationResults = validateAlert(this.alert);
      if (validationResults.frequency === false) {
        this.wizardSteps.FrequencyStep.isValid = false;
      } else if (this.alert.id || this.wizardDetails.clone) {
        this.wizardSteps.FrequencyStep.isValid = validationResults.frequency;
      }
    },
    tripWiresProcessingUpdate({ processing }) {
      this.wizardSteps.TripWiresStep.processing = processing;
    },
    frequencyStepFinished({ isValid }) {
      this.wizardSteps.FrequencyStep.isValid = isValid;
    },
    sharedStepFinished({ isValid }) {
      this.wizardSteps.ShareStep.isValid = isValid;
    },
    async saveAlertFunc() {
      const validationResults = validateAlert(this.alert);
      if (validationResults.overall) {
        return await this.$store.dispatch(
          MODULE_PREFIX_ALERTS + SAVE_USER_ALERT,
          this.alert
        );
      }
      const errorMessages = [];
      if (!validationResults.aoi) {
        errorMessages.push("Selected AOI");
      }
      if (!validationResults.type) {
        errorMessages.push("Tripwires");
      }
      if (!validationResults.share) {
        errorMessages.push("Share Information");
      }
      if (!validationResults.name) {
        errorMessages.push("Name");
      }
      this.displayError = true;
      this.errorMessage =
        "Please fix the following: " + errorMessages.join(", ");
    },
    routeToPrevious() {
      switch (this.currentStep) {
        case this.wizardSteps.TripWiresStep.id:
          this.currentStep = this.wizardSteps.AoiStep.id;
          break;
        case this.wizardSteps.FrequencyStep.id:
          this.currentStep = this.wizardSteps.TripWiresStep.id;
          break;
        case this.wizardSteps.ShareStep.id:
          this.currentStep = this.wizardSteps.FrequencyStep.id;
          break;
        case this.wizardSteps.SummaryStep.id:
          this.currentStep = this.wizardSteps.ShareStep.id;
          break;
      }
    },
    routeToNext() {
      switch (this.currentStep) {
        case this.wizardSteps.AoiStep.id:
          this.currentStep = this.wizardSteps.TripWiresStep.id;
          break;
        case this.wizardSteps.TripWiresStep.id:
          this.currentStep = this.wizardSteps.FrequencyStep.id;
          break;
        case this.wizardSteps.FrequencyStep.id:
          this.wizardSteps.ShareStep.isValid = true;
          this.currentStep = this.wizardSteps.ShareStep.id;
          break;
        case this.wizardSteps.ShareStep.id:
          this.currentStep = this.wizardSteps.SummaryStep.id;
          break;
        case this.wizardSteps.SummaryStep.id:
          this.saveAlertFunc();
          break;
        default:
          this.currentStep = this.wizardSteps.AoiStep.id;
          break;
      }
    },
    routeToSummary() {
      this.currentStep = this.wizardSteps.SummaryStep.id;
    },
  },
  created() {
    if (!this.wizardDetails.alert) {
      return;
    }
    this.alert = JSON.parse(JSON.stringify(this.wizardDetails.alert));
    if (this.wizardDetails.clone) {
      this.alert.id = null;
      this.alert.auditInfo = null;
      this.alert.owner = {
        email: this.profile.email,
        id: this.profile.user_id,
      };
      this.alert.unsubToken = null;
      this.alert.share = {
        type: { id: 1, value: "no", display: "No Thanks" },
        list: [],
      };

      this.alert.name = "(CLONE) " + this.alert.name;
    }
    const validationResults = validateAlert(this.alert);
    this.wizardSteps.AoiStep.isValid = validationResults.aoi;
    this.wizardSteps.TripWiresStep.isValid = validationResults.type;
    this.wizardSteps.ShareStep.isValid = validationResults.share;
    this.wizardSteps.FrequencyStep.isValid = validationResults.frequency;
    this.wizardSteps.SummaryStep.isValid = validationResults.overall;
    if (validationResults.overall) {
      this.currentStep = this.wizardSteps.SummaryStep.id;
    }
  },
};
</script>
<style>
.wizard-alert-name.v-input .v-input__slot {
  border-radius: 10px !important;
  border-color: rgba(0, 0, 0, 0.38) !important;
}
</style>
<style scoped>
.alert-wizard-container {
  padding: 0;
  position: relative;
  height: 90vh;
}
.wizard-exit-btn {
  color: rgba(0, 0, 0, 0.7) !important;
}
.wizard-container {
  background-color: #f7f7fa !important;
  border: none !important;
  box-shadow: none;
  height: 90vh;
}
.alert-wizard-content-container {
  padding: 0;
  height: 65vh;
  overflow-y: auto;
}
</style>
